import React, { useState, useMemo } from 'react';
import ToolLayout from './ToolLayout';

const CHANNELS = [
  {
    id: 'theatrical',
    label: 'Theatrical',
    desc: 'Cinema release + P&A',
    tag: 'HIGH RISK / HIGH REWARD',
    netLow: 0.05,
    netHigh: 0.35,
    color: 'sky',
    barColor: 'bg-sky-500',
    note: 'Filmmaker net after theater split (~50%), distributor (~30%), and P&A recoupment.',
  },
  {
    id: 'svod',
    label: 'SVOD',
    desc: 'Streaming flat license deal',
    tag: 'UPFRONT / STABLE',
    netLow: 0.12,
    netHigh: 0.40,
    color: 'violet',
    barColor: 'bg-violet-500',
    note: 'One-time license fee. Filmmaker retains ~75–85% after sales agent commission.',
  },
  {
    id: 'tvod',
    label: 'TVOD',
    desc: 'Digital rental & purchase',
    tag: 'PASSIVE / LONG-TAIL',
    netLow: 0.03,
    netHigh: 0.12,
    color: 'emerald',
    barColor: 'bg-emerald-500',
    note: 'Platform keeps ~30%, aggregator ~15%. Filmmaker nets ~50–55% of gross.',
  },
  {
    id: 'avod',
    label: 'AVOD',
    desc: 'Ad-supported free streaming',
    tag: 'LOW / PASSIVE',
    netLow: 0.01,
    netHigh: 0.06,
    color: 'amber',
    barColor: 'bg-amber-500',
    note: 'Revenue-share on ad impressions. Low ceiling but zero-friction discovery.',
  },
  {
    id: 'physical',
    label: 'Physical / Retail',
    desc: 'DVD, Blu-ray, retail chains',
    tag: 'DECLINING MARKET',
    netLow: 0.02,
    netHigh: 0.10,
    color: 'orange',
    barColor: 'bg-orange-500',
    note: 'Retailer margin ~45%, distributor ~25%. Filmmaker nets ~25–35% of wholesale.',
  },
  {
    id: 'international',
    label: 'International Sales',
    desc: 'Foreign territory licensing',
    tag: 'HIGH UPSIDE',
    netLow: 0.08,
    netHigh: 0.40,
    color: 'rose',
    barColor: 'bg-rose-500',
    note: 'Highly variable. Sales agent takes 20–25%. Strong genre films overperform.',
  },
];

const TAG_COLORS = {
  'HIGH RISK / HIGH REWARD': 'text-red-400 border-red-900',
  'UPFRONT / STABLE': 'text-violet-400 border-violet-900',
  'PASSIVE / LONG-TAIL': 'text-emerald-400 border-emerald-900',
  'LOW / PASSIVE': 'text-amber-400 border-amber-900',
  'DECLINING MARKET': 'text-orange-400 border-orange-900',
  'HIGH UPSIDE': 'text-rose-400 border-rose-900',
};

function fmt(n) {
  return '$' + Math.round(n).toLocaleString('en-US');
}

export default function DistributionRevenueModeler() {
  const [budget, setBudget] = useState(500000);
  const [active, setActive] = useState(new Set(['svod', 'tvod', 'international']));
  const [expanded, setExpanded] = useState(null);

  const toggle = (id) => {
    setActive((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const { channels, totalLow, totalHigh } = useMemo(() => {
    const channels = CHANNELS.map((c) => ({
      ...c,
      netLowAmt: budget * c.netLow,
      netHighAmt: budget * c.netHigh,
    }));
    const activeChannels = channels.filter((c) => active.has(c.id));
    const totalLow = activeChannels.reduce((s, c) => s + c.netLowAmt, 0);
    const totalHigh = activeChannels.reduce((s, c) => s + c.netHighAmt, 0);
    return { channels, totalLow, totalHigh };
  }, [budget, active]);

  const maxHigh = Math.max(...CHANNELS.map((c) => budget * c.netHigh));

  return (
    <ToolLayout
      accent="Aperture Slate // Distribution Tools"
      title="Revenue"
      titleAccent="Modeler"
      description="Select your distribution channels and see projected filmmaker net based on your production budget."
    >
      <div className="border border-zinc-800 bg-zinc-950 p-8 space-y-8">

        {/* Budget */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold">Production Budget</label>
            <span className="text-2xl font-black tabular-nums">{fmt(budget)}</span>
          </div>
          <input
            type="range" min={50000} max={5000000} step={25000} value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
            className="w-full h-1 appearance-none bg-zinc-800 rounded-full accent-sky-500 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-zinc-600 mt-1 tracking-wider">
            <span>$50K</span><span>$5M</span>
          </div>
        </div>

        {/* Channel toggles */}
        <div>
          <label className="block text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-3">
            Distribution Channels — select all that apply
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                onClick={() => toggle(c.id)}
                className={`py-3 px-4 text-left border transition-all duration-200 ${
                  active.has(c.id)
                    ? 'border-sky-500 bg-sky-500/10'
                    : 'border-zinc-800 hover:border-zinc-600'
                }`}
              >
                <div className={`text-[11px] font-black tracking-wider ${active.has(c.id) ? 'text-sky-400' : 'text-zinc-500'}`}>
                  {c.label}
                </div>
                <div className="text-[10px] text-zinc-600 mt-0.5">{c.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-zinc-800" />

        {/* Channel breakdown */}
        <div>
          <p className="text-[11px] tracking-widest text-zinc-400 uppercase font-bold mb-5">
            Filmmaker Net by Channel
          </p>
          <div className="space-y-5">
            {channels.map((c) => {
              const isActive = active.has(c.id);
              const isExpanded = expanded === c.id;
              return (
                <div
                  key={c.id}
                  className={`transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-25'}`}
                >
                  <div
                    className="flex items-center justify-between mb-1 cursor-pointer"
                    onClick={() => setExpanded(isExpanded ? null : c.id)}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold tracking-widest border px-2 py-0.5 ${TAG_COLORS[c.tag]}`}>
                        {c.tag}
                      </span>
                      <span className="text-[11px] text-zinc-400 tracking-widest uppercase font-semibold">{c.label}</span>
                    </div>
                    <span className="text-xs text-zinc-400 tabular-nums">
                      {fmt(c.netLowAmt)} – {fmt(c.netHighAmt)}
                    </span>
                  </div>
                  {/* Low bar */}
                  <div className="h-1.5 w-full bg-zinc-900 rounded-sm overflow-hidden mb-1">
                    <div
                      className={`h-full rounded-sm transition-all duration-700 opacity-40 ${c.barColor}`}
                      style={{ width: `${(c.netLowAmt / maxHigh) * 100}%` }}
                    />
                  </div>
                  {/* High bar */}
                  <div className="h-2 w-full bg-zinc-900 rounded-sm overflow-hidden">
                    <div
                      className={`h-full rounded-sm transition-all duration-700 ${c.barColor}`}
                      style={{ width: `${(c.netHighAmt / maxHigh) * 100}%` }}
                    />
                  </div>
                  {isExpanded && (
                    <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">{c.note}</p>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-zinc-600 mt-3">Light bar = conservative scenario. Solid bar = optimistic scenario. Tap a row for methodology.</p>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-zinc-800 bg-zinc-900/50 p-4 text-center">
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Conservative Net</p>
            <p className="text-xl font-black text-white tabular-nums">{fmt(totalLow)}</p>
          </div>
          <div className="border border-zinc-800 bg-zinc-900/50 p-4 text-center">
            <p className="text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Optimistic Net</p>
            <p className="text-xl font-black text-sky-400 tabular-nums">{fmt(totalHigh)}</p>
          </div>
        </div>

        <p className="text-[10px] text-zinc-600 leading-relaxed tracking-wide border-t border-zinc-900 pt-4">
          Filmmaker net estimates are directional and based on industry averages. Actual revenue depends heavily on marketing spend, talent, critical reception, and distribution deal terms. Consult a film attorney and entertainment accountant before making financial projections.
        </p>
      </div>
    </ToolLayout>
  );
}
